import {
  Box,
  Card,
  CardContent,
  Skeleton,
  Tooltip,
  Typography,
  useMediaQuery,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import PercentIcon from "@mui/icons-material/Percent";
import BuildOutlinedIcon from "@mui/icons-material/BuildOutlined";
import { useEffect, useMemo, useRef, useState, type ElementType } from "react";
import { useTranslation } from "react-i18next";
import { LoginResponse } from "subModule/src/services/userProfile/interface";
import { EnqueueSnackbar } from "notistack";
import AlertMessages from "common/Alert/AlertMessages";
import { Controller, useForm } from "react-hook-form";
import DynamicSelect from "common/Select/Select";
import { GetTechnicianProductivityResponse } from "subModule/src/services/analytics/interface";
import { useGetTechnicianProductivityService } from "subModule/src/services/analytics/useAnalytics";
import { InfiniteData } from "@tanstack/react-query";
import { GetStationDropdownResponse } from "subModule/src/services/station/interface";
import {
  dashboardWidgetHeaderSx,
  dashboardWidgetFiltersSx,
  dashboardWidgetTitleSx,
  dashboardWidgetIconSx,
  dashboardWidgetTitleRowSx,
} from "components/Analytics/dashboardWidgetLayout";

interface TechnicianProductivityProps {
  user: LoginResponse | undefined;
  themes: any;
  stations: { id: number; name: string }[] | [];
  enqueueSnackbar: EnqueueSnackbar;
  stationsLoaded: boolean;
  handleStationActions: (
    typeName: string,
    action: "searchIt" | "loadNext",
    search?: string,
  ) => void;
  setPageNo: any;
  stationsData: InfiniteData<GetStationDropdownResponse, unknown> | undefined;
  searchLoading: string | null;
  pageNo: number;
  dropdownSearchTerm: any;
  setDropdownSearchTerm: any;
  globalFilters: { stationId: any; range: any; allStation: boolean };
  globalFilterApplied: boolean;
  setGlobalFilterApplied: (value: boolean) => void;
}

type ProductivityData = GetTechnicianProductivityResponse["data"];

const emptyData = (): ProductivityData => ({
  mttrHours: 0,
  laborEfficiencyPercent: 0,
  technicianUtilizationPercent: 0,
  reworkRatePercent: 0,
});

const compactSelectSx = {
  "& .css-7896aj-control, & .css-1xiok1u-control": {
    minHeight: "auto",
    fontSize: '9px',
    height: "auto",
    overflow: "hidden",
    "& .css-yzr845-indicatorContainer, & .css-1rr3n57-indicatorContainer, & .css-1asjhht-indicatorContainer, & .css-5s13ue-indicatorContainer":
      {
        background: "#fff",
        padding: 0,
        width: "15px",
        height: "15px",
      },
    "& .css-pogzpp-loadingIndicator, & .css-o9ehiq-loadingIndicator": {
      position: "absolute",
      right: 0,
    },
    border: 0,
  },
};

export default function TechnicianProductivity(
  props: TechnicianProductivityProps,
) {
  const { t, i18n } = useTranslation();
  const firstRender = useRef(true);
  const screenWidth = useMediaQuery(
    "(min-width: 1200px) and (max-width: 1400px)",
  );
  const [data, setData] = useState<ProductivityData>(emptyData());
  const [loading, setLoading] = useState(true);
  const {
    control,
    formState: { errors },
    setValue,
    watch,
  } = useForm<{ [key: string]: any }>({
    defaultValues: {
      range: { value: "monthly", label: t("mtd") },
      allStation: false,
    },
  });
  const filters = useMemo(
    () => [
      { value: "daily", label: t("today") },
      { value: "yesterday", label: t("yesterday") },
      { value: "weekly", label: t("wtd") },
      { value: "monthly", label: t("mtd") },
      { value: "last_month", label: t("last_month") },
      { value: "yearly", label: t("ytd") },
    ],
    [i18n.language],
  );
  const values = watch(["stationId", "range", "allStation"]);
  const primary = props.themes?.primary || "#7C3AED";
  const secondary = props.themes?.secondary || "#EA580C";

  const { refetch: apiRefetch } = useGetTechnicianProductivityService({
    stationId: values[0]?.value || null,
    range: values[1]?.value,
    allStation: values[2],
  });

  useEffect(() => {
    if (firstRender.current && props.stationsLoaded === true) {
      firstRender.current = false;
      setValue(
        "stationId",
        (props?.stations?.length > 1
          ? props?.stations?.[1]
          : props?.stations?.[0]) || null,
      );
      setTimeout(() => fetchData(), 50);
    }
  }, [props.stations]);

  useEffect(() => {
    const currentRange = watch("range");
    const updated = filters.find((f) => f.value === currentRange?.value);
    if (updated) setValue("range", updated);
  }, [i18n.language, filters]);

  useEffect(() => {
    if (props.globalFilterApplied === true) {
      setValue("stationId", props.globalFilters.stationId);
      setValue("range", props.globalFilters.range);
      setValue("allStation", props.globalFilters.allStation);
      setTimeout(() => fetchData(), 100);
    }
  }, [props.globalFilterApplied]);

  const fetchData = async () => {
    setLoading(true);
    await apiRefetch()
      .then((res: GetTechnicianProductivityResponse | unknown | any) => {
        if (res?.data?.success) {
          setData(res?.data?.data || emptyData());
        } else {
          props.enqueueSnackbar(
            <AlertMessages
              text={res?.data?.message || "Something went wrong"}
            />,
            { variant: "error" },
          );
        }
      })
      .catch((err) => {
        console.error("===", err);
      })
      .finally(() => {
        setLoading(false);
        if (props.globalFilterApplied === true)
          props.setGlobalFilterApplied(false);
      });
  };

  const formatValue = (value: number) =>
    new Intl.NumberFormat("en", {
      maximumFractionDigits: value % 1 === 0 ? 0 : 2,
    }).format(Number(value || 0));

  const metrics: {
    key: keyof ProductivityData;
    labelKey: string;
    tooltipKey: string;
    suffix: string;
    color: string;
    bg: string;
    Icon: ElementType;
  }[] = [
    {
      key: "mttrHours",
      labelKey: "mttrHours",
      tooltipKey: "mttrHoursInfo",
      suffix: " h",
      color: primary,
      bg: `${primary}14`,
      Icon: AccessTimeIcon,
    },
    {
      key: "laborEfficiencyPercent",
      labelKey: "laborEfficiency",
      tooltipKey: "laborEfficiencyInfo",
      suffix: "%",
      color: secondary,
      bg: `${secondary}14`,
      Icon: PercentIcon,
    },
    {
      key: "technicianUtilizationPercent",
      labelKey: "technicianUtilization",
      tooltipKey: "technicianUtilizationInfo",
      suffix: "%",
      color: "#2563EB",
      bg: "#DBEAFE",
      Icon: PercentIcon,
    },
    {
      key: "reworkRatePercent",
      labelKey: "reworkRate",
      tooltipKey: "reworkRateInfo",
      suffix: "%",
      color: "#E11D48",
      bg: "#FFE4E6",
      Icon: PercentIcon,
    },
  ];

  return (
    <Card
      sx={{
        height: "100%",
        minHeight: 270,
        boxShadow: "0 1px 3px rgba(16, 24, 40, 0.06)",
        borderRadius: "12px",
        overflow: "hidden",
        "& .MuiCardContent-root": {
          padding: "16px !important",
          height: "100%",
          boxSizing: "border-box",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        },
      }}
    >
      <CardContent>
        <Box
          sx={{ ...dashboardWidgetHeaderSx, flexShrink: 0, mb: 1.5 }}
        >
          <Box sx={dashboardWidgetTitleRowSx}>
            <Box
              sx={{
                ...dashboardWidgetIconSx,
                width: 36,
                height: 36,
                borderRadius: "10px",
                backgroundColor: `${primary}1F`,
                color: primary,
                
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
                "& svg": { fontSize: 17, color: primary },
              }}
            >
              <BuildOutlinedIcon />
            </Box>
            <Box
              sx={{
                minWidth: 0,
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                gap: "2px",
              }}
            >
              <Typography
                className="font-Saira text-left"
                sx={{
                  fontSize: { xs: 12, sm: 15 },
              fontWeight: { xs: 600, sm: 700 },
              textAlign: "left",
                  color: "#0F2744",
                  lineHeight: 1.25,
                }}
              >
                {t("technicianProductivity")}
              </Typography>
              <Typography
                className="font-Saira text-left"
                sx={{
                  fontSize: 11,
                  fontWeight: 500,
                  color: "#9CA3AF",
                  lineHeight: 1.3,
                }}
              >
                {t("performanceAndEfficiency")}
              </Typography>
            </Box>
          </Box>

          <Box className="flex" sx={{ ...dashboardWidgetFiltersSx, flexShrink: 0 }}>
            <Controller
              name="stationId"
              control={control}
              defaultValue={null}
              rules={{ required: t("This field is required.") }}
              render={({ field }) => (
                <Box sx={compactSelectSx}>
                  <DynamicSelect
                    label={""}
                    {...field}
                    options={props?.stations || []}
                    isDisabled={loading}
                    onChange={(selectedOption: any) => {
                      field.onChange(selectedOption);
                      if (selectedOption?.value === 0)
                        setValue("allStation", true);
                      else setValue("allStation", false);
                      setTimeout(() => fetchData(), 50);
                    }}
                    error={errors.stationId?.message as string}
                    ddWidth="30px"
                    ddHeight="30px"
                    defaultPlaceholder={t("station")}
                    isLoading={props.searchLoading === "technicianProductivity"}
                    onInputChange={(
                      search: string,
                      { action }: { action: string },
                    ) => {
                      if (
                        action === "input-change" ||
                        (action !== "set-value" &&
                          action !== "menu-close" &&
                          action !== "input-blur")
                      )
                        props.handleStationActions(
                          "technicianProductivity",
                          "searchIt",
                          search,
                        );
                      else if (
                        action === "input-blur" ||
                        action === "menu-close"
                      ) {
                        if (props.dropdownSearchTerm || props.pageNo > 1)
                          props.handleStationActions(
                            "technicianProductivity",
                            "searchIt",
                            "",
                          );
                        else props.setDropdownSearchTerm("");
                        props.setPageNo(1);
                      }
                    }}
                    onMenuScrollToBottom={() => {
                      if (!(props.stationsData?.pages[0]?.data as any)?.last)
                        props.handleStationActions(
                          "technicianProductivity",
                          "loadNext",
                        );
                    }}
                    page={props.pageNo}
                  />
                </Box>
              )}
            />
            <Controller
              name="range"
              control={control}
              defaultValue={null}
              rules={{ required: t("This field is required.") }}
              render={({ field }) => (
                <Box
                  sx={{
                    ...compactSelectSx,
                    "& .css-7896aj-control, & .css-1xiok1u-control": {
                      ...compactSelectSx[
                        "& .css-7896aj-control, & .css-1xiok1u-control"
                      ],
                      width: screenWidth ? "60px" : "auto",
                    },
                  }}
                >
                  <DynamicSelect
                    label={""}
                    {...field}
                    options={filters}
                    onChange={(selectedOption: any) => {
                      field.onChange(selectedOption);
                      setTimeout(() => fetchData(), 50);
                    }}
                    error={errors.range?.message as string}
                    ddWidth="30px"
                    ddHeight="30px"
                    isDisabled={loading}
                  />
                </Box>
              )}
            />
          </Box>
        </Box>

        {loading ? (
          <Box
            className="grid gap-2"
            sx={{
              gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
              flex: 1,
            }}
          >
            {[0, 1, 2, 3].map((i) => (
              <Skeleton
                key={i}
                variant="rounded"
                height={78}
                sx={{
                  backgroundColor: secondary,
                  borderRadius: "12px",
                }}
              />
            ))}
          </Box>
        ) : (
          <Box
            className="grid gap-2"
            sx={{
              gridTemplateColumns: {
                xs: "1fr",
                sm: "repeat(2, minmax(0, 1fr))",
              },
              flex: 1,
              minHeight: 0,
              overflowY: "auto",
              alignContent: "start",
              pr: 0.5,
            }}
          >
            {metrics.map((metric) => {
              const value = Number(data?.[metric.key] || 0);
              const Icon = metric.Icon;
              return (
                <Box
                  key={metric.key}
                  sx={{
                    borderRadius: "12px",
                    backgroundColor: metric.bg,
                    px: 1.5,
                    py: 1.25,
                    display: "flex",
                    flexDirection: "column",
                    gap: 0.75,
                    minWidth: 0,
                  }}
                >
                  <Box className="flex items-center gap-2">
                    {/* <Box
                      sx={{
                        width: 28,
                        height: 28,
                        borderRadius: "8px",
                        backgroundColor: `${primary}18`,
                        color: primary,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                      }}
                    >
                      <Icon sx={{ fontSize: 14 }} />
                    </Box> */}
                    <Tooltip
                      placement="top"
                      title={`${formatValue(value)}${metric.suffix.trim()}`}
                      enterDelay={0}
                    >
                      <Typography
                        className="font-Saira"
                        sx={{
                          fontSize: 19,
                          fontWeight: 800,
                          color: metric.color,
                          lineHeight: 1.1,
                        }}
                      >
                        {formatValue(value)}
                        <Typography
                          component="span"
                          className="font-Saira"
                          sx={{ fontSize: 12, fontWeight: 600, ml: 0.25 }}
                        >
                          {metric.suffix.trim()}
                        </Typography>
                      </Typography>
                    </Tooltip>
                  </Box>

                  <Box className="flex items-center gap-1">
                    <Typography
                      className="font-Saira"
                      sx={{
                        fontSize: 11,
                        fontWeight: 600,
                        color: "#6B7280",
                        lineHeight: 1.2,
                      }}
                    >
                      {t(metric.labelKey)}
                    </Typography>
                    <Tooltip
                      title={
                        <Typography
                          className="font-Saira"
                          sx={{ fontSize: 11, color: "#111827", p: "4px" }}
                          dir={
                            i18n.language === "ar" || i18n.language === "ur"
                              ? "rtl"
                              : "ltr"
                          }
                        >
                          {t(metric.tooltipKey)}
                        </Typography>
                      }
                      enterDelay={0}
                      placement="top"
                      arrow
                      componentsProps={{
                        tooltip: {
                          sx: {
                            backgroundColor: "#fff",
                            boxShadow: "0px 2px 8px rgba(0,0,0,0.12)",
                            maxWidth: 320,
                          },
                        },
                      }}
                    >
                      <InfoOutlinedIcon
                        sx={{
                          fontSize: 13,
                          color: "#9CA3AF",
                          cursor: "pointer",
                          flexShrink: 0,
                        }}
                      />
                    </Tooltip>
                  </Box>
                </Box>
              );
            })}
          </Box>
        )}
      </CardContent>
    </Card>
  );
}
